import React from 'react';
import { Chip, Box, Typography } from '@mui/material';
import { Payment, CreditCard } from '@mui/icons-material';

interface PaymentProviderBadgeProps {
  order: any;
  showDetails?: boolean;
  size?: 'small' | 'medium';
}

export const PaymentProviderBadge: React.FC<PaymentProviderBadgeProps> = ({
  order,
  showDetails = false,
  size = 'small'
}) => {
  const getPaymentProvider = () => {
    if (!order) return null;
    
    if (order.stripePaymentIntentId || order.stripeCheckoutSessionId) {
      return 'stripe';
    }
    if (order.transactionId && !order.stripePaymentIntentId) {
      return 'authorizeNet';
    }
    return null;
  };

  const getPaymentStatus = () => {
    if (!order) return 'unknown';
    
    if (order.orderStatus === 'Completed') {
      return 'paid';
    }
    if (order.stripePaymentIntentId && order.orderStatus !== 'Completed') {
      return 'pre_authorized';
    }
    return 'pending';
  };

  const provider = getPaymentProvider();
  const status = getPaymentStatus();

  if (!provider) {
    return (
      <Typography
        variant={size === 'small' ? 'body2' : 'body1'}
        color="text.secondary"
        sx={{ fontStyle: 'italic' }}
      >
        No Payment
      </Typography>
    );
  }

  const getChipProps = () => {
    const baseProps = {
      size,
      icon: provider === 'stripe' ? <Payment /> : <CreditCard />
    };

    switch (status) {
      case 'paid':
        return {
          ...baseProps,
          label: provider === 'stripe' ? 'Stripe (Paid)' : 'Authorize.net (Paid)',
          color: 'success' as const,
          variant: 'filled' as const
        };
      case 'pre_authorized':
        return {
          ...baseProps,
          label: 'Stripe (Pre-authorized)',
          color: 'warning' as const,
          variant: 'filled' as const
        };
      case 'pending':
        return {
          ...baseProps,
          label: provider === 'stripe' ? 'Stripe (Pending)' : 'Authorize.net (Pending)',
          color: 'info' as const,
          variant: 'outlined' as const
        };
      default:
        return {
          ...baseProps,
          label: provider === 'stripe' ? 'Stripe' : 'Authorize.net',
          color: 'default' as const,
          variant: 'outlined' as const
        };
    }
  };

  const chipProps = getChipProps();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip {...chipProps} />
      {showDetails && provider === 'stripe' && order.stripePaymentIntentId && (
        <Typography variant="caption" color="text.secondary">
          {order.stripePaymentIntentId.slice(-8)}
        </Typography>
      )}
      {showDetails && provider === 'authorizeNet' && order.transactionId && (
        <Typography variant="caption" color="text.secondary">
          {order.transactionId.slice(-8)}
        </Typography>
      )}
    </Box>
  );
}; 

